import { useRef, useState } from "react";
import SyringeFillDiagram from "@/components/injection/SyringeFillDiagram";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Member-specific protocol card. The single source of truth for what to
// inject, how much water to add, how much to draw, and when. Used in
// onboarding step 3, the reference library, and the dashboard.
// Renders empty-state copy when the BE hasn't generated a protocol yet
// (waiting on approval or initial physician sign-off).
//
// When `showDownload` is true we render a "DOWNLOAD PDF" action that
// renders the card to PDF via dynamically-imported html2pdf.js (kept
// out of the main bundle to keep first paint snappy).
export default function ProtocolCard({ protocol, compact, showDownload = false, testId = "protocol-card" }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!protocol) {
    return (
      <div
        data-testid={`${testId}-empty`}
        style={{
          padding: 24,
          background: C.bone,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          color: C.warmGray,
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        Your physician is still finalizing your protocol. We'll fill this card in the moment it's ready, and email you with next steps.
      </div>
    );
  }

  const recon = protocol.reconstitution || {};
  const sched = protocol.schedule || {};
  const vial = protocol.vial || {};

  const downloadPdf = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Dynamic import — html2pdf.js + jspdf are ~250kb gzipped, no
      // reason to ship them to members who never download.
      const mod = await import("html2pdf.js");
      const html2pdf = mod.default || mod;
      const filename = `voya9-protocol-${(protocol.compound || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: "jpeg", quality: 0.96 },
          html2canvas: { scale: 2, backgroundColor: "#FFFFFF", useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        })
        .from(cardRef.current)
        .save();
      toast("Protocol card downloaded.", "success");
    } catch (err) {
      toast("Couldn't generate PDF — try your browser's Print to PDF instead.", "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid={`${testId}-wrap`}>
    <article
      ref={cardRef}
      data-testid={testId}
      style={{
        background: C.pure,
        border: `2px solid ${C.ink}`,
        overflow: "hidden",
      }}
    >
      {/* Inky banner — compound name + brand */}
      <header style={{ background: C.ink, color: C.cream, padding: "20px 22px" }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peach }}>
          ★ YOUR PROTOCOL
        </div>
        <h2 style={{ margin: "6px 0 0", fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          {protocol.compound}
        </h2>
        {protocol.brandName && protocol.brandName !== protocol.compound && (
          <div style={{ marginTop: 4, fontSize: 13, color: "rgba(250,246,240,0.7)", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            also known as {protocol.brandName}
          </div>
        )}
      </header>

      <div style={{ padding: 22, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 18 }}>
        <Field label="DOSE" value={`${protocol.dose}${protocol.doseUnit ? ` ${protocol.doseUnit}` : " mg"}`} />
        <Field label="FREQUENCY" value={formatFrequency(sched, protocol.frequency)} />
        {protocol.nextDueAt && <Field label="NEXT DUE" value={formatNext(protocol.nextDueAt)} />}
      </div>

      {!compact && (recon.bacWaterMl || recon.drawVolumeMl) && (
        <div style={{ padding: "0 22px 22px" }}>
          <SectionTitle>RECONSTITUTION</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            {recon.bacWaterMl != null && <Field label="ADD" value={`${recon.bacWaterMl}ml bacteriostatic water`} />}
            {recon.finalConcentrationMgMl != null && (
              <Field label="FINAL CONCENTRATION" value={`${recon.finalConcentrationMgMl}mg/ml`} />
            )}
            {recon.drawVolumeMl != null && <Field label="DRAW PER DOSE" value={`${recon.drawVolumeMl}ml`} highlight />}
          </div>
          {recon.drawVolumeMl != null && (
            <div style={{ marginTop: 18 }}>
              <SyringeFillDiagram
                drawVolumeMl={recon.drawVolumeMl}
                syringeUnits={recon.syringeUnits}
                imageUrl={recon.syringeImageUrl}
              />
            </div>
          )}
        </div>
      )}

      {!compact && vial && (vial.reconstitutedAt || vial.expiresAt || vial.dosesRemaining != null) && (
        <div style={{ padding: "0 22px 22px" }}>
          <SectionTitle>YOUR CURRENT VIAL</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            {vial.reconstitutedAt && <Field label="RECONSTITUTED" value={formatDate(vial.reconstitutedAt)} />}
            {vial.expiresAt && <Field label="EXPIRES" value={formatDate(vial.expiresAt)} accent={daysUntil(vial.expiresAt) <= 7} />}
            {vial.dosesRemaining != null && <Field label="DOSES REMAINING" value={`~${vial.dosesRemaining}`} />}
          </div>
          {vial.expiresAt && daysUntil(vial.expiresAt) <= 7 && daysUntil(vial.expiresAt) >= 0 && (
            <p data-testid="vial-expiration-warning" style={{ marginTop: 12, padding: 14, background: C.peach, color: C.peachDark, fontSize: 13, lineHeight: 1.55 }}>
              <strong style={{ color: C.ink }}>Expires in {daysUntil(vial.expiresAt)} day{daysUntil(vial.expiresAt) === 1 ? "" : "s"}.</strong> Use remaining doses, and your next vial will ship shortly.
            </p>
          )}
          {vial.expiresAt && daysUntil(vial.expiresAt) < 0 && (
            <p data-testid="vial-expired-alert" style={{ marginTop: 12, padding: 14, background: C.coral, color: C.cream, fontSize: 13, lineHeight: 1.55 }}>
              <strong>This vial has passed its 30-day reconstitution period.</strong> Don't use past expiration. Contact your care team for guidance.
            </p>
          )}
        </div>
      )}

      <footer style={{ borderTop: `1px dashed ${C.warmGray3}`, padding: "16px 22px", fontSize: 12, color: C.warmGray, lineHeight: 1.55 }}>
        Always cross-check this card against the Foundations guide before injecting.
        {protocol.careTeamPhone && (
          <> Questions? Message your care team — or call <strong style={{ color: C.ink }}>{protocol.careTeamPhone}</strong>.</>
        )}
      </footer>
    </article>
    {showDownload && (
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={downloadPdf}
          disabled={downloading}
          data-testid={`${testId}-download-pdf`}
          style={{
            padding: "12px 22px",
            background: C.ink,
            color: C.cream,
            border: 0,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            cursor: downloading ? "wait" : "pointer",
            opacity: downloading ? 0.6 : 1,
          }}
        >
          {downloading ? "PREPARING PDF…" : "↓ DOWNLOAD AS PDF"}
        </button>
      </div>
    )}
    </div>
  );
}

function Field({ label, value, highlight, accent }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>{label}</div>
      <div
        style={{
          marginTop: 4,
          fontSize: highlight ? 22 : 16,
          fontWeight: highlight ? 800 : 700,
          color: accent ? C.coral : C.ink,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ marginTop: 24, marginBottom: 14, fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
      ★ {children}
    </h3>
  );
}

function formatFrequency(sched, fallback) {
  if (sched?.intervalDays === 1) return "Daily";
  if (sched?.intervalDays === 7) return "Weekly";
  if (sched?.intervalDays && sched.intervalDays > 1) return `Every ${sched.intervalDays} days`;
  return fallback || "Per physician";
}

function formatNext(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86400000);
}
